import React, { useState } from 'react';

const GAP = 2;
const RADIUS = 8;
const REORDER_DRAG_TYPE = 'application/x-cool-media-reorder';

function ComposeCell({
  url,
  isVideo,
  index,
  total,
  onRemove,
  onMove,
  canReorder,
  dragReorderIndex,
  dragOverIndex,
  setDragReorderIndex,
  setDragOverIndex,
  onReorder,
  style,
  overlayExtra,
}) {
  const isDragOver = dragOverIndex === index && dragReorderIndex !== index;

  return (
    <div
      draggable={canReorder}
      onDragStart={(e) => {
        if (!canReorder) return;
        setDragReorderIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(REORDER_DRAG_TYPE, String(index));
        e.dataTransfer.setData('text/plain', String(index));
      }}
      onDragOver={(e) => {
        if (!canReorder) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverIndex !== index) setDragOverIndex(index);
      }}
      onDragLeave={() => {
        if (dragOverIndex === index) setDragOverIndex(null);
      }}
      onDrop={(e) => {
        if (!canReorder) return;
        e.preventDefault();
        e.stopPropagation();
        const raw =
          e.dataTransfer.getData(REORDER_DRAG_TYPE) ||
          e.dataTransfer.getData('text/plain');
        const fromIndex = Number(raw);
        if (!Number.isNaN(fromIndex)) onReorder(fromIndex, index);
        setDragReorderIndex(null);
        setDragOverIndex(null);
      }}
      onDragEnd={() => {
        setDragReorderIndex(null);
        setDragOverIndex(null);
      }}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: '#0f172a',
        minWidth: 0,
        minHeight: 0,
        opacity: dragReorderIndex === index ? 0.55 : 1,
        outline: isDragOver ? '2px solid #0284c7' : 'none',
        outlineOffset: -2,
        cursor: canReorder ? 'grab' : 'default',
        userSelect: 'none',
        ...style,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 6,
          left: 6,
          background: 'rgba(15, 23, 42, 0.78)',
          color: '#fff',
          borderRadius: 999,
          minWidth: 22,
          height: 22,
          padding: '0 6px',
          fontSize: 11,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        {index + 1}
      </span>

      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          background: 'rgba(0,0,0,0.65)',
          color: '#fff',
          border: 'none',
          borderRadius: '50%',
          width: 26,
          height: 26,
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: '26px',
          zIndex: 2,
        }}
        aria-label="Remove"
      >
        ×
      </button>

      {canReorder && (
        <div
          style={{
            position: 'absolute',
            bottom: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 4,
            zIndex: 2,
          }}
        >
          <button
            type="button"
            disabled={index === 0}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onMove(index, -1);
            }}
            title="Move earlier"
            style={arrowBtn(index === 0)}
          >
            ←
          </button>
          <button
            type="button"
            disabled={index === total - 1}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onMove(index, 1);
            }}
            title="Move later"
            style={arrowBtn(index === total - 1)}
          >
            →
          </button>
        </div>
      )}

      {url && !isVideo ? (
        <img
          src={url}
          alt=""
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            pointerEvents: 'none',
          }}
        />
      ) : url ? (
        <>
          <video
            src={url}
            muted
            playsInline
            preload="metadata"
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              pointerEvents: 'none',
              background: '#0f172a',
            }}
          />
          <span
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 28,
              textShadow: '0 1px 4px rgba(0,0,0,0.6)',
              pointerEvents: 'none',
            }}
          >
            ▶
          </span>
        </>
      ) : null}

      {overlayExtra}
    </div>
  );
}

function arrowBtn(disabled) {
  return {
    background: disabled ? 'rgba(15,23,42,0.35)' : 'rgba(15,23,42,0.78)',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    width: 26,
    height: 22,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 12,
    lineHeight: '22px',
    padding: 0,
  };
}

/**
 * Facebook-style collage preview while composing a post.
 * items: [{ id, url, isVideo }]
 */
export default function MediaComposePreview({
  items,
  onRemove,
  onReorder,
  onMove,
}) {
  const [dragReorderIndex, setDragReorderIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  if (!Array.isArray(items) || items.length === 0) return null;

  const count = items.length;
  const canReorder = count > 1;
  const cellProps = {
    canReorder,
    dragReorderIndex,
    dragOverIndex,
    setDragReorderIndex,
    setDragOverIndex,
    onReorder,
    onRemove,
    onMove,
    total: count,
  };

  const wrap = (children, height) => (
    <div style={{ marginBottom: 10 }}>
      <p
        style={{
          margin: '0 0 8px',
          fontSize: 12,
          color: '#64748b',
        }}
      >
        {count > 1
          ? 'Drag to rearrange. Layout matches how your post will look in the feed.'
          : 'Add more photos or videos to build a collage like Facebook.'}
      </p>
      <div
        style={{
          width: '100%',
          borderRadius: RADIUS,
          overflow: 'hidden',
          background: '#e4e6eb',
          height,
        }}
      >
        {children}
      </div>
    </div>
  );

  if (count === 1) {
    return wrap(
      <ComposeCell
        {...cellProps}
        url={items[0].url}
        isVideo={items[0].isVideo}
        index={0}
        style={{ width: '100%', height: '100%' }}
      />,
      280
    );
  }

  if (count === 2) {
    return wrap(
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: GAP,
          height: '100%',
        }}
      >
        {items.map((item, i) => (
          <ComposeCell
            key={item.id}
            {...cellProps}
            url={item.url}
            isVideo={item.isVideo}
            index={i}
          />
        ))}
      </div>,
      240
    );
  }

  if (count === 3) {
    return wrap(
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas: `
            "main sideTop"
            "main sideBot"
          `,
          gap: GAP,
          height: '100%',
        }}
      >
        <ComposeCell
          {...cellProps}
          url={items[0].url}
          isVideo={items[0].isVideo}
          index={0}
          style={{ gridArea: 'main' }}
        />
        <ComposeCell
          {...cellProps}
          url={items[1].url}
          isVideo={items[1].isVideo}
          index={1}
          style={{ gridArea: 'sideTop' }}
        />
        <ComposeCell
          {...cellProps}
          url={items[2].url}
          isVideo={items[2].isVideo}
          index={2}
          style={{ gridArea: 'sideBot' }}
        />
      </div>,
      300
    );
  }

  if (count === 4) {
    return wrap(
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: GAP,
          height: '100%',
        }}
      >
        {items.map((item, i) => (
          <ComposeCell
            key={item.id}
            {...cellProps}
            url={item.url}
            isVideo={item.isVideo}
            index={i}
          />
        ))}
      </div>,
      280
    );
  }

  // 5+: Facebook collage for first 5; extra items as a strip below
  const remaining = count - 5;
  const visible = items.slice(0, 5);
  const extras = items.slice(5);

  return (
    <div style={{ marginBottom: 10 }}>
      <p style={{ margin: '0 0 8px', fontSize: 12, color: '#64748b' }}>
        Drag to rearrange. Layout matches how your post will look in the feed.
      </p>
      <div
        style={{
          width: '100%',
          borderRadius: RADIUS,
          overflow: 'hidden',
          background: '#e4e6eb',
          height: 300,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gridTemplateRows: '1.15fr 1fr',
            gridTemplateAreas: `
              "a a a b b b"
              "c c d d e e"
            `,
            gap: GAP,
            height: '100%',
          }}
        >
          {visible.map((item, i) => {
            const area = ['a', 'b', 'c', 'd', 'e'][i];
            return (
              <ComposeCell
                key={item.id}
                {...cellProps}
                url={item.url}
                isVideo={item.isVideo}
                index={i}
                style={{ gridArea: area }}
                overlayExtra={
                  i === 4 && remaining > 0 ? (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        color: '#fff',
                        fontSize: 22,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      +{remaining}
                    </div>
                  ) : null
                }
              />
            );
          })}
        </div>
      </div>
      {extras.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 8,
          }}
        >
          {extras.map((item, i) => {
            const index = i + 5;
            return (
              <ComposeCell
                key={item.id}
                {...cellProps}
                url={item.url}
                isVideo={item.isVideo}
                index={index}
                style={{ width: 72, height: 72, borderRadius: 6 }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export { REORDER_DRAG_TYPE };
